import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Card, Image } from 'react-bootstrap';
import type { LeadCloudFileType } from '@econtractor/types';
import SpinnerBlock from 'components/others/SpinnerBlock';
import { formatFileSize } from 'utils/helpers/gimHelper';
import { getFileExtension } from './helper/filesUtils';
import ItemDropdown from './ItemDropdown';
import type { FileDragData } from './model/fileDropRules';

interface FileItemProps {
    file: LeadCloudFileType;
    galleryId: string;
    canDelete?: boolean;
    busy?: boolean;
    onDelete?: () => void;
    onRename?: () => void;
    showPreview?: boolean;
    canDrag?: boolean;
    overlay?: boolean;
}

const FileItem = ({
    file,
    galleryId,
    canDelete,
    busy,
    onDelete,
    onRename,
    showPreview,
    canDrag,
    overlay,
}: FileItemProps) => {
    const extension = getFileExtension(file.fileName);
    const [src, setSrc] = useState(`/imgs/fileTypes/${extension}.png`);
    const fileSizeLabel = formatFileSize(file.fileSize);
    const dragEnabled = Boolean(canDrag) && !overlay;

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: overlay ? `overlay:${file.storageKey}` : file.storageKey,
        data: { type: 'file', item: file } satisfies FileDragData,
        disabled: !dragEnabled,
    });

    const cardClass = [
        'file-card m-0 h-100 position-relative',
        dragEnabled ? 'file-card--draggable' : '',
        isDragging ? 'file-card--dragging' : '',
        overlay ? 'file-card--overlay' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={setNodeRef}
            className={['h-100', dragEnabled ? 'file-card-dnd' : '', isDragging ? 'file-card--dragging' : '']
                .filter(Boolean)
                .join(' ')}
            {...(dragEnabled ? { ...attributes, ...listeners } : {})}
        >
            <Card className={cardClass}>
                <SpinnerBlock show={busy} />
                <Card.Body className="p-2 file-card-body">
                    <div className="file-card-thumb">
                        {showPreview ? (
                            <a
                                className="glightbox d-flex h-100 w-100 align-items-center justify-content-center"
                                href={file.publicUrl}
                                target="_blank"
                                rel="noreferrer"
                                data-gallery={galleryId}
                                title={file.fileName}
                            >
                                <img className="rounded" src={file.publicUrl} alt={file.fileName} />
                            </a>
                        ) : (
                            <a
                                href={file.publicUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={file.fileName}
                                className="file-card-thumb--icon d-flex h-100 w-100 align-items-center justify-content-center"
                            >
                                <Image
                                    className="rounded"
                                    onError={() => setSrc('/imgs/fileTypes/unknown.png')}
                                    src={src}
                                    alt={extension || file.fileName}
                                />
                            </a>
                        )}
                    </div>
                    <div className="file-card-meta min-w-0">
                        <h6 className="d-grid mb-0">
                            <a
                                href={file.publicUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-truncate w-100 no-select text-body text-decoration-none"
                                title={file.fileName}
                            >
                                {file.fileName}
                            </a>
                        </h6>
                        {fileSizeLabel ? (
                            <p className="mb-0 text-muted">
                                <small>{fileSizeLabel}</small>
                            </p>
                        ) : null}
                    </div>
                    {overlay ? null : (
                        <ItemDropdown
                            canDelete={canDelete}
                            canRename={canDelete}
                            deleting={busy}
                            copyUrl={file.publicUrl}
                            onDelete={onDelete}
                            onRename={onRename}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default FileItem;
